/** What a business sells besides sessions and events. */
export enum OfferKind {
  ENTRY_PACK = "entry_pack",
  TRAINING_PLAN = "training_plan",
  DIET_PLAN = "diet_plan",
  CLIENT_SUBSCRIPTION = "client_subscription",
}

/** What an order is for, which decides how it is fulfilled once paid. */
export enum OrderItemKind {
  /** A visit of a session type; holds a slot while the client pays. */
  SESSION = "session",
  /** One client's place at an event. */
  EVENT_SEAT = "event_seat",
  /** A listed offer bought from the trainer page. */
  OFFER = "offer",
  /** A payment link the business sent for an entry pack it already created. */
  ENTRY_PACK_REQUEST = "entry_pack_request",
  /** A visit already booked, paid ahead of its deadline ("pay before the visit"). */
  VISIT_PREPAYMENT = "visit_prepayment",
  /** A product from the business's shop: a file to download or something shipped. */
  PRODUCT = "product",
}

/** What a shop product is, which decides how the buyer gets it. */
export enum ProductKind {
  /** A file the buyer downloads once paid: an ebook, a guide, a video course. */
  DIGITAL = "digital",
  /** Something the business ships to the address the buyer gives at checkout: merch, equipment. */
  PHYSICAL = "physical",
}

export enum OrderStatus {
  PENDING = "pending",
  PAID = "paid",
  FAILED = "failed",
  EXPIRED = "expired",
  CANCELLED = "cancelled",
  PARTIALLY_REFUNDED = "partially_refunded",
  REFUNDED = "refunded",
}

/** The state of money that arrived. */
export enum PaymentRecordStatus {
  SUCCEEDED = "succeeded",
  PARTIALLY_REFUNDED = "partially_refunded",
  REFUNDED = "refunded",
  DISPUTED = "disputed",
  DISPUTE_LOST = "dispute_lost",
}

export enum RefundStatus {
  /** We asked Stripe; the webhook has not confirmed it yet. */
  PENDING = "pending",
  SUCCEEDED = "succeeded",
  FAILED = "failed",
  CANCELED = "canceled",
}

export enum RefundInitiator {
  CLIENT_CANCEL = "client_cancel",
  BUSINESS = "business",
  NO_SHOW = "no_show",
  DISPUTE = "dispute",
  /** We refunded on our own, e.g. a payment that completed after its slot hold expired. */
  SYSTEM = "system",
}

/** How a session type or event seat is paid for: the trainer's payment rule. */
export enum PaymentMode {
  /** On the day, however the business takes money. */
  AT_VISIT = "at_visit",
  /** Online, to get the slot. Needs a payment account. */
  AT_BOOKING = "at_booking",
  /** By a deadline before the visit: online, or by transfer the team records. */
  BEFORE_VISIT = "before_visit",
  /** Only with an entry pack of at least a minimum size. Single visits are not sold. */
  PACK_ONLY = "pack_only",
}

/** Where a payment came from. */
export enum PaymentSource {
  /** Through Stripe, on the business's payment account. */
  ONLINE = "online",
  /** Taken by the team and written down: cash, a transfer, a card terminal. */
  RECORDED = "recorded",
}

/** How the team says a recorded payment was made. */
export enum RecordedPaymentMethod {
  CASH = "cash",
  BANK_TRANSFER = "bank_transfer",
  CARD_TERMINAL = "card_terminal",
  /** Let go for free: settles the visit or pack with no money. */
  WAIVED = "waived",
  OTHER = "other",
}

export enum InvoiceDeliveryDestination {
  ACCOUNTANT_EMAIL = "accountant_email",
}

export enum InvoiceDeliveryStatus {
  PENDING = "pending",
  SENT = "sent",
  FAILED = "failed",
}


/** Where an invoice came from. */
export enum InvoiceSource {
  /** Stripe's own invoice document (older one-off sales, subscription periods): a payment confirmation. */
  STRIPE = "stripe",
  /** Issued by the business in its own tool and uploaded (ADR 0019). */
  UPLOADED = "uploaded",
  /** Issued in the app, numbered and drawn from its snapshot (ADR 0034). */
  ISSUED = "issued",
}

/** Where a client's request for an invoice stands. */
export enum InvoiceRequestStatus {
  OPEN = "open",
  /** Answered: the app issued the invoice, or the business uploaded one. */
  ISSUED = "issued",
}
